import { gql } from '@apollo/client/core';
import { CameraSearchFragment, SnapshotsFieldFragment } from './cameras';
import { DoorSearchFragment } from './doors';
import {
  IntrusionPanelDeviceFieldsFragment,
  IntrusionPartitionStateFieldsFragment,
  IntrusionZoneAssignmentFragment,
  IntrusionZoneAssignmentSummaryFragment
} from './intrusion-fragments';

const GenericDeviceFragment = gql`
  fragment GenericDeviceFragment on qx_Device {
    id
    kind
    enabled
    name
    profileMSupported
    description
    oldestMedia {
      type
      date
    }
    gatewayID
    gateway {
      id
      name
      connectionStatus
      syncStatus
      type
      nics
      hostname
      placeID
      place {
        id
        name
        hierarchy {
          name
        }
      }
    }
    sensors(order_by: { name: asc }) {
      id
      name
      type
      enabled
      place {
        id
        name
        hierarchy {
          name
        }
      }
      device {
        id
        deviceAddress
      }
    }
    doors(order_by: { name: asc }) {
      id
      name
      type
      enabled
      disabledByLicense
      lockdownImmunity
      canOperate
      mode
      state
      alarm
      doorMonitor
      doorMonitorAddress
      deviceConnectionStatus
      gatewayConnectionStatus
      # used by children table panel on device page
      device {
        id
        deviceAddress
      }
      # used by children table panel on device page
      place {
        id
        name
        hierarchy {
          name
        }
      }
      outside {
        id
        name
        hierarchy {
          name
        }
      }
    }
    videoSources(order_by: { name: asc }) {
      id
      name
      enabled
      deviceConnectionStatus
      gatewayConnectionStatus
      profileMSupported
      metadataEnabled
      mediaProfiles(order_by: [{ refID: asc }]) {
        id
        refID
        streamEnabled
        streamStatus
        deletedAt
      }
      audioSources {
        audioSourceID
        audioSource {
          id
          enabled
        }
      }
      # used by children table panel on device page
      device {
        id
        deviceAddress
        kind
        enabled
      }
      # used by children table panel on device page
      place {
        id
        name
        hierarchy {
          name
        }
      }
    }
    audioSources(order_by: {name: asc }) {
      id
      name
      refID
      enabled
      mediaProfiles {
        id
        audioEncoderSettings
        videoSourceID
      }
    }
    deviceAnalyticSources {
      id
      analyticSource {
        id
        comment
        value
      }
    }
    port
    tls
    make
    model
    serial
    deviceMac
    deviceAddress
    deviceUsername
    devicePassword
    firmware
    pacsConfigurationID
    meta
    connectionStatus
    createdAt
    pacsStatus
    pacsHWUpdate
  }
`;

export const DeviceSearchFragment = gql`
  fragment DeviceSearchFragment on qx_Device {
    id
    kind
    enabled
    name
    description
    deviceAddress
    deviceMac
    port
    serial
    tls
    meta
    make
    model
    firmware
    ntp
    timezone
    dst
    pacsStatus
    pacsHWUpdate
    connectionStatus
    profileMSupported
    gateway {
      id
      name
      connectionStatus
      syncStatus
      type
      nics
      place {
        id
        name
        hierarchy {
          name
        }
      }
    }
    doors {
      id
      name
      enabled
    }
    videoSources {
      id
      name
      enabled
    }
    intrusionPartitionCount: IntrusionPartitions {
      index
      stateValues
      alarmValues
    }
    intrusionZoneCount: IntrusionZones {
      index
      stateValues
      alarmValues
    }
  }
`;

export const IntrusionPanelSearchFragment = gql`
  fragment IntrusionPanelSearchFragment on qx_Device {
    ...DeviceSearchFragment
  }
  ${DeviceSearchFragment}
`;

const LiveScrollerDeviceResolveFragment = gql`
  fragment LiveScrollerDeviceResolveFragment on qx_Device {
    id
    name
    kind
  }
`;

export const GQL_GET_DEVICES_BY_IDS = gql`
  query GQL_GET_DEVICES_BY_IDS($ids: [String!]!) {
    devices(where: { id: { _in: $ids } }) {
      ...LiveScrollerDeviceResolveFragment
    }
  }
  ${LiveScrollerDeviceResolveFragment}
`;

export const GQL_GET_AI_TOOL_RESULT_DEVICES_BY_IDS = gql`
  query GQL_GET_AI_TOOL_RESULT_DEVICES_BY_IDS($ids: [String!]!) {
    devices(where: { id: { _in: $ids } }) {
      ...DeviceSearchFragment
      doors {
        ...DoorSearchFragment
      }
      videoSources {
        ...CameraSearchFragment
        ...SnapshotsFieldFragment
      }
    }
  }
  ${DeviceSearchFragment}
  ${DoorSearchFragment}
  ${CameraSearchFragment}
  ${SnapshotsFieldFragment}
`;

export const GQL_GET_DEVICE_BY_ID = gql`
  query GQL_GET_DEVICE_BY_ID($id: String!) {
    device(id: $id) {
      ...GenericDeviceFragment
    }
  }
  ${GenericDeviceFragment}
`;

export const GQL_SUB_DEVICE_BY_ID = gql`
  subscription GQL_SUB_DEVICE_BY_ID($id: String!) {
    device(id: $id) {
      ...GenericDeviceFragment
    }
  }
  ${GenericDeviceFragment}
`;

export const GQL_UPDATE_DEVICE_BY_ID = gql`
  mutation GQL_UPDATE_DEVICE_BY_ID($id: String!, $device: qx_Device_set_input) {
    updateDevice(pk_columns: { id: $id }, _set: $device) {
      id
    }
  }
`;

export const GQL_GET_DEVICE_PROXY_URL = gql`
  query GQL_GET_DEVICE_PROXY_URL($id: String!) {
    device(id: $id) {
      id
      proxyUrl
    }
  }
`;

export const GQL_CHECK_DEVICES_FOR_UNIQUE_GATEWAY_IP_PORT = gql`
  query GQL_CHECK_DEVICES_FOR_UNIQUE_GATEWAY_IP_PORT($where: qx_Device_bool_exp) {
    devices(where: $where) {
      id
    }
  }
`;

export const GQL_DEVICE_SEARCH = gql`
  query GQL_DEVICE_SEARCH($where: qx_Device_bool_exp, $limit: Int, $offset: Int, $orderBy: [qx_Device_order_by!]) {
    devices(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...DeviceSearchFragment
      videoSources {
        id
        name
        enabled
        ...SnapshotsFieldFragment
      }
    }
    deviceAggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
  ${DeviceSearchFragment}
  ${SnapshotsFieldFragment}
`;

export const GQL_INTRUSION_PANEL_SEARCH = gql`
  query GQL_INTRUSION_PANEL_SEARCH(
    $where: qx_Device_bool_exp
    $limit: Int
    $offset: Int
    $orderBy: [qx_Device_order_by!]
  ) {
    devices(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...IntrusionPanelSearchFragment
    }
    deviceAggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
  ${IntrusionPanelSearchFragment}
`;

const IntrusionPanelDetailFragment = gql`
  fragment IntrusionPanelDetailFragment on qx_Device {
    ...DeviceSearchFragment
    dscConnections {
      integrationKey
    }
    IntrusionPartitions(
      where: { enabled: { _eq: true } }
      order_by: [{ index: asc }, { name: asc }]
    ) {
      ...IntrusionPartitionStateFieldsFragment
      enabled
      canArm
      canDisarm
      PartitionZoneAssignments(where: { enabled: { _eq: true } }) {
        ...IntrusionZoneAssignmentFragment
      }
    }
  }
  ${DeviceSearchFragment}
  ${IntrusionPartitionStateFieldsFragment}
  ${IntrusionZoneAssignmentFragment}
`;

export const GQL_GET_INTRUSION_PANEL_BY_ID = gql`
  query GQL_GET_INTRUSION_PANEL_BY_ID($id: String!) {
    device(id: $id) {
      ...IntrusionPanelDetailFragment
    }
  }
  ${IntrusionPanelDetailFragment}
`;

export const GQL_SUB_INTRUSION_PANEL_BY_ID = gql`
  subscription GQL_SUB_INTRUSION_PANEL_BY_ID($id: String!) {
    device(id: $id) {
      ...IntrusionPanelDetailFragment
    }
  }
  ${IntrusionPanelDetailFragment}
`;

const IntrusionPartitionEditableFieldsFragment = gql`
  fragment IntrusionPartitionEditableFieldsFragment on IntrusionPartition {
    id
    description
    placeID
    Place {
      id
      name
      hierarchy {
        name
      }
    }
  }
`;

export const IntrusionPartitionSearchFragment = gql`
  fragment IntrusionPartitionSearchFragment on IntrusionPartition {
    ...IntrusionPartitionEditableFieldsFragment
    ...IntrusionPartitionStateFieldsFragment
    enabled
    PartitionZoneAssignments(where: { enabled: { _eq: true } }) {
      zoneID
      IntrusionZone {
        id
        stateValues
        alarmValues
      }
    }
    Device {
      ...IntrusionPanelDeviceFieldsFragment
      deviceAddress
    }
  }
  ${IntrusionPartitionEditableFieldsFragment}
  ${IntrusionPartitionStateFieldsFragment}
  ${IntrusionPanelDeviceFieldsFragment}
`;

export const GQL_INTRUSION_PARTITION_SEARCH = gql`
  query GQL_INTRUSION_PARTITION_SEARCH(
    $where: IntrusionPartition_bool_exp
    $limit: Int
    $offset: Int
    $orderBy: [IntrusionPartition_order_by!]
  ) {
    IntrusionPartition(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...IntrusionPartitionSearchFragment
    }
  }
  ${IntrusionPartitionSearchFragment}
`;

export const GQL_SUB_INTRUSION_PARTITION_SEARCH = gql`
  subscription GQL_SUB_INTRUSION_PARTITION_SEARCH(
    $where: IntrusionPartition_bool_exp
    $limit: Int
    $offset: Int
    $orderBy: [IntrusionPartition_order_by!]
  ) {
    IntrusionPartition(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...IntrusionPartitionSearchFragment
    }
  }
  ${IntrusionPartitionSearchFragment}
`;

const IntrusionPartitionDetailFragment = gql`
  fragment IntrusionPartitionDetailFragment on IntrusionPartition {
    ...IntrusionPartitionEditableFieldsFragment
    ...IntrusionPartitionStateFieldsFragment
    enabled
    canArm
    canDisarm
    PartitionZoneAssignments(where: { enabled: { _eq: true } }) {
      ...IntrusionZoneAssignmentFragment
    }
    Device {
      ...IntrusionPanelDeviceFieldsFragment
      description
      IntrusionPartitions(where: { enabled: { _eq: true } }, order_by: [{ index: asc }, { name: asc }]) {
        ...IntrusionPartitionStateFieldsFragment
        PartitionZoneAssignments(where: { enabled: { _eq: true } }) {
          ...IntrusionZoneAssignmentSummaryFragment
        }
      }
    }
  }
  ${IntrusionPartitionEditableFieldsFragment}
  ${IntrusionPartitionStateFieldsFragment}
  ${IntrusionPanelDeviceFieldsFragment}
  ${IntrusionZoneAssignmentFragment}
  ${IntrusionZoneAssignmentSummaryFragment}
`;

export const GQL_GET_INTRUSION_PARTITION_BY_ID = gql`
  query GQL_GET_INTRUSION_PARTITION_BY_ID($id: String!) {
    IntrusionPartition_by_pk(id: $id) {
      ...IntrusionPartitionDetailFragment
    }
  }
  ${IntrusionPartitionDetailFragment}
`;

export const GQL_SUB_INTRUSION_PARTITION_BY_ID = gql`
  subscription GQL_SUB_INTRUSION_PARTITION_BY_ID($id: String!) {
    IntrusionPartition_by_pk(id: $id) {
      ...IntrusionPartitionDetailFragment
    }
  }
  ${IntrusionPartitionDetailFragment}
`;

export const GQL_UPDATE_INTRUSION_PARTITION_BY_ID = gql`
  mutation GQL_UPDATE_INTRUSION_PARTITION_BY_ID($id: String!, $partition: IntrusionPartition_set_input!) {
    update_IntrusionPartition_by_pk(pk_columns: { id: $id }, _set: $partition) {
      ...IntrusionPartitionEditableFieldsFragment
    }
  }
  ${IntrusionPartitionEditableFieldsFragment}
`;

export const GQL_ARM_INTRUSION_PARTITION = gql`
  mutation GQL_ARM_INTRUSION_PARTITION($deviceID: String!, $partitionID: String!, $armMode: IntrusionArmMode!) {
    armPartition(deviceID: $deviceID, partitionID: $partitionID, armMode: $armMode) {
      success
    }
  }
`;

export const GQL_BYPASS_INTRUSION_ZONE = gql`
  mutation GQL_BYPASS_INTRUSION_ZONE($deviceID: String!, $zoneID: String!) {
    bypassZone(deviceID: $deviceID, zoneID: $zoneID) {
      success
    }
  }
`;

export const GQL_UPDATE_DEVICE_WITH_CONNECTION = gql`
  mutation GQL_UPDATE_DEVICE_WITH_CONNECTION($input: UpdateDeviceWithConnectionInput!) {
    updateDeviceWithConnection(input: $input) {
      success
    }
  }
`;

export const GQL_AXIS_BODY_WORN_USER_LIST = gql`
  query GQL_AXIS_BODY_WORN_USER_LIST($deviceID: String!) {
    axisBodyWornUserList(deviceID: $deviceID) {
      id
      name
    }
  }
`;

export const GQL_DEVICE_SEARCH_SUBSCRIPTION = gql`
  subscription GQL_DEVICE_SEARCH_SUBSCRIPTION(
    $where: qx_Device_bool_exp
    $limit: Int
    $offset: Int
    $orderBy: [qx_Device_order_by!]
  ) {
    devices(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...DeviceSearchFragment
    }
  }
  ${DeviceSearchFragment}
`;
